import { useState, useEffect } from 'react';
import { useApi } from '../../services/api';
import {
    LeaveRequest,
    LeaveType,
    LeaveStatus,
    Holiday
} from '@bst-stellar/shared';
import { formatDate } from '../../utils/dateFormatter';

export default function LeaveManagement() {
    const api = useApi();
    const [activeTab, setActiveTab] = useState<'requests' | 'types' | 'holidays'>('requests');
    const [requests, setRequests] = useState<LeaveRequest[]>([]);
    const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState<string>('pending');

    // Action state
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState<string | null>(null);

    // Leave Type Modal State
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [editingType, setEditingType] = useState<LeaveType | null>(null);
    const [typeForm, setTypeForm] = useState<Partial<LeaveType>>({
        name: '',
        code: '',
        annualQuota: 0,
        monthlyAccrual: 0,
        quarterlyAccrual: 0,
        carryForward: false,
        maxCarryForward: 0,
        requiresApproval: true,
        isActive: true,
        displayOrder: 0
    });

    // Holiday Modal State
    const [showHolidayModal, setShowHolidayModal] = useState(false);
    const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
    const [holidayForm, setHolidayForm] = useState<Partial<Holiday>>({
        name: '',
        date: new Date(),
        isOptional: false
    });

    useEffect(() => {
        loadData();
    }, [activeTab, filterStatus]);

    async function loadData() {
        try {
            setLoading(true);
            if (activeTab === 'requests') {
                const data = await api.getLeaveRequests({ status: filterStatus === 'all' ? undefined : filterStatus });
                setRequests(data.requests);
            } else if (activeTab === 'types') {
                const data = await api.getLeaveTypes();
                setLeaveTypes(data.types);
            } else if (activeTab === 'holidays') {
                const data = await api.getHolidays();
                setHolidays(data.holidays);
            }
        } catch (err) {
            console.error('Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    }

    // --- Request Actions ---

    async function handleApprove(id: string) {
        try {
            setProcessingId(id);
            await api.updateLeaveStatus(id, LeaveStatus.APPROVED);
            loadData();
        } catch (err) {
            console.error('Failed to approve:', err);
        } finally {
            setProcessingId(null);
        }
    }

    async function handleReject(id: string) {
        try {
            setProcessingId(id);
            await api.updateLeaveStatus(id, LeaveStatus.REJECTED, rejectionReason);
            setShowRejectModal(null);
            setRejectionReason('');
            loadData();
        } catch (err) {
            console.error('Failed to reject:', err);
        } finally {
            setProcessingId(null);
        }
    }

    // --- Leave Type Actions ---

    function openTypeModal(type?: LeaveType) {
        if (type) {
            setEditingType(type);
            setTypeForm(type);
        } else {
            setEditingType(null);
            setTypeForm({
                name: '',
                code: '',
                annualQuota: 0,
                monthlyAccrual: 0,
                quarterlyAccrual: 0,
                carryForward: false,
                maxCarryForward: 0,
                requiresApproval: true,
                isActive: true,
                displayOrder: leaveTypes.length + 1
            });
        }
        setShowTypeModal(true);
    }

    async function handleSaveType(e: React.FormEvent) {
        e.preventDefault();
        try {
            setProcessingId('save-type');
            if (editingType) {
                await api.updateLeaveType(editingType.id, typeForm);
            } else {
                await api.createLeaveType(typeForm);
            }
            setShowTypeModal(false);
            loadData();
        } catch (err) {
            console.error('Failed to save leave type:', err);
        } finally {
            setProcessingId(null);
        }
    }

    async function handleDeleteType(id: string) {
        if (!confirm('Are you sure you want to delete this leave type?')) return;
        try {
            setProcessingId(id);
            await api.deleteLeaveType(id);
            loadData();
        } catch (err) {
            console.error('Failed to delete leave type:', err);
        } finally {
            setProcessingId(null);
        }
    }

    // --- Holiday Actions ---

    function openHolidayModal(holiday?: Holiday) {
        if (holiday) {
            setEditingHoliday(holiday);
            setHolidayForm({
                ...holiday,
                date: new Date(holiday.date) // Ensure date object
            });
        } else {
            setEditingHoliday(null);
            setHolidayForm({
                name: '',
                date: new Date(),
                isOptional: false
            });
        }
        setShowHolidayModal(true);
    }

    async function handleSaveHoliday(e: React.FormEvent) {
        e.preventDefault();
        try {
            setProcessingId('save-holiday');
            if (editingHoliday) {
                await api.updateHoliday(editingHoliday.id, holidayForm);
            } else {
                await api.createHoliday(holidayForm);
            }
            setShowHolidayModal(false);
            loadData();
        } catch (err) {
            console.error('Failed to save holiday:', err);
        } finally {
            setProcessingId(null);
        }
    }

    async function handleDeleteHoliday(id: string) {
        if (!confirm('Are you sure you want to delete this holiday?')) return;
        try {
            setProcessingId(id);
            await api.deleteHoliday(id);
            loadData();
        } catch (err) {
            console.error('Failed to delete holiday:', err);
        } finally {
            setProcessingId(null);
        }
    }

    const getStatusColor = (status: LeaveStatus) => {
        switch (status) {
            case LeaveStatus.APPROVED: return 'text-green-400 bg-green-400/10';
            case LeaveStatus.REJECTED: return 'text-red-400 bg-red-400/10';
            case LeaveStatus.PENDING: return 'text-yellow-400 bg-yellow-400/10';
            case LeaveStatus.CANCELLED: return 'text-gray-400 bg-gray-400/10';
            default: return 'text-gray-400';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-text-primary">Leave Management</h1>
                {activeTab === 'types' && (
                    <button
                        onClick={() => openTypeModal()}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm"
                    >
                        + Add Leave Type
                    </button>
                )}
                {activeTab === 'holidays' && (
                    <button
                        onClick={() => openHolidayModal()}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm"
                    >
                        + Add Holiday
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 bg-surface p-1 rounded-lg border border-border w-fit">
                {(['requests', 'types', 'holidays'] as const).map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab
                            ? 'bg-primary text-white shadow-sm'
                            : 'text-text-secondary hover:text-text-primary hover:bg-background'
                            }`}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="bg-surface rounded-xl border border-border shadow-sm min-h-[400px]">
                {loading ? (
                    <div className="p-8 text-center text-text-muted">Loading...</div>
                ) : (
                    <>
                        {activeTab === 'requests' && (
                            <div>
                                <div className="p-4 border-b border-border flex gap-4">
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="bg-background border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                                    >
                                        <option value="pending">Pending Requests</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="all">All Requests</option>
                                    </select>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-background">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Employee</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Dates</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Days</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                            {requests.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="px-6 py-8 text-center text-text-muted">
                                                        No requests found
                                                    </td>
                                                </tr>
                                            ) : (
                                                requests.map((req) => (
                                                    <tr key={req.id} className="hover:bg-background/50 transition-colors">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-text-primary">{req.userName}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                            {req.leaveTypeName}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                            {formatDate(req.startDate)}
                                                            {!req.isHalfDay && req.startDate !== req.endDate && ` - ${formatDate(req.endDate)}`}
                                                            {req.isHalfDay && <span className="ml-2 text-xs bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded">Half Day</span>}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                                                            {req.days}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(req.status)}`}>
                                                                {req.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                            {req.status === LeaveStatus.PENDING && (
                                                                <div className="flex gap-2">
                                                                    <button
                                                                        onClick={() => handleApprove(req.id)}
                                                                        disabled={!!processingId}
                                                                        className="text-green-400 hover:text-green-300 disabled:opacity-50"
                                                                    >
                                                                        Approve
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setShowRejectModal(req.id)}
                                                                        disabled={!!processingId}
                                                                        className="text-red-400 hover:text-red-300 disabled:opacity-50"
                                                                    >
                                                                        Reject
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {activeTab === 'types' && (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-background">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Code</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Quota</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Accrual (M/Q)</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {leaveTypes.map((type) => (
                                            <tr key={type.id} className="hover:bg-background/50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">{type.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{type.code}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{type.annualQuota || 'Unlimited'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{type.monthlyAccrual} / {type.quarterlyAccrual}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${type.isActive ? 'text-green-400 bg-green-400/10' : 'text-gray-400 bg-gray-400/10'}`}>
                                                        {type.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-2">
                                                    <button onClick={() => openTypeModal(type)} className="text-primary hover:text-primary-light">Edit</button>
                                                    <button onClick={() => handleDeleteType(type.id)} className="text-red-400 hover:text-red-300">Delete</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {activeTab === 'holidays' && (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-background">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {holidays.map((holiday) => (
                                            <tr key={holiday.id} className="hover:bg-background/50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text-primary">{holiday.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{formatDate(holiday.date)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">{holiday.isOptional ? 'Optional' : 'Mandatory'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-2">
                                                    <button onClick={() => openHolidayModal(holiday)} className="text-primary hover:text-primary-light">Edit</button>
                                                    <button onClick={() => handleDeleteHoliday(holiday.id)} className="text-red-400 hover:text-red-300">Delete</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Reject Modal */}
            {
                showRejectModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-surface rounded-xl max-w-md w-full p-6 shadow-xl border border-border">
                            <h2 className="text-xl font-bold text-text-primary mb-4">Reject Request</h2>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-text-primary focus:ring-2 focus:ring-primary focus:outline-none h-24 resize-none mb-4"
                                placeholder="Reason for rejection..."
                                required
                            />
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowRejectModal(null)}
                                    className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleReject(showRejectModal)}
                                    disabled={!rejectionReason.trim() || !!processingId}
                                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Leave Type Modal */}
            {
                showTypeModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-surface rounded-xl max-w-lg w-full p-6 shadow-xl border border-border max-h-[90vh] overflow-y-auto">
                            <h2 className="text-xl font-bold text-text-primary mb-4">{editingType ? 'Edit Leave Type' : 'Add Leave Type'}</h2>
                            <form onSubmit={handleSaveType} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-text-secondary mb-1">Name</label>
                                        <input
                                            type="text"
                                            value={typeForm.name}
                                            onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                                            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-text-secondary mb-1">Code</label>
                                        <input
                                            type="text"
                                            value={typeForm.code}
                                            onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value })}
                                            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-text-secondary mb-1">Annual Quota</label>
                                        <input
                                            type="number"
                                            value={typeForm.annualQuota}
                                            onChange={(e) => setTypeForm({ ...typeForm, annualQuota: parseFloat(e.target.value) })}
                                            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-text-secondary mb-1">Monthly Accrual</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={typeForm.monthlyAccrual}
                                            onChange={(e) => setTypeForm({ ...typeForm, monthlyAccrual: parseFloat(e.target.value) })}
                                            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-text-secondary mb-1">Quarterly Accrual</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={typeForm.quarterlyAccrual}
                                            onChange={(e) => setTypeForm({ ...typeForm, quarterlyAccrual: parseFloat(e.target.value) })}
                                            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 text-sm text-text-primary">
                                        <input
                                            type="checkbox"
                                            checked={typeForm.isActive}
                                            onChange={(e) => setTypeForm({ ...typeForm, isActive: e.target.checked })}
                                            className="rounded border-border bg-background text-primary focus:ring-primary"
                                        />
                                        Active
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-text-primary">
                                        <input
                                            type="checkbox"
                                            checked={typeForm.requiresApproval}
                                            onChange={(e) => setTypeForm({ ...typeForm, requiresApproval: e.target.checked })}
                                            className="rounded border-border bg-background text-primary focus:ring-primary"
                                        />
                                        Requires Approval
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-text-primary">
                                        <input
                                            type="checkbox"
                                            checked={typeForm.carryForward}
                                            onChange={(e) => setTypeForm({ ...typeForm, carryForward: e.target.checked })}
                                            className="rounded border-border bg-background text-primary focus:ring-primary"
                                        />
                                        Carry Forward
                                    </label>
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowTypeModal(false)}
                                        className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!!processingId}
                                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                                    >
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* Holiday Modal */}
            {
                showHolidayModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-surface rounded-xl max-w-md w-full p-6 shadow-xl border border-border">
                            <h2 className="text-xl font-bold text-text-primary mb-4">{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h2>
                            <form onSubmit={handleSaveHoliday} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1">Name</label>
                                    <input
                                        type="text"
                                        value={holidayForm.name}
                                        onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                                        className="w-full bg-background border border-border rounded-lg px-3 py-2 text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-text-secondary mb-1">Date</label>
                                    <input
                                        type="date"
                                        value={holidayForm.date ? new Date(holidayForm.date).toISOString().split('T')[0] : ''}
                                        onChange={(e) => setHolidayForm({ ...holidayForm, date: new Date(e.target.value) })}
                                        className="w-full bg-background border border-border rounded-lg px-3 py-2 text-text-primary focus:ring-2 focus:ring-primary focus:outline-none"
                                        required
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="isOptional"
                                        checked={holidayForm.isOptional}
                                        onChange={(e) => setHolidayForm({ ...holidayForm, isOptional: e.target.checked })}
                                        className="rounded border-border bg-background text-primary focus:ring-primary"
                                    />
                                    <label htmlFor="isOptional" className="text-sm text-text-primary">Optional Holiday</label>
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowHolidayModal(false)}
                                        className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!!processingId}
                                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                                    >
                                        Save
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </div >
    );
}
